import { useState, useRef, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface UploadedDoc {
  id: string;
  requirement: string;
  fileName: string;
  filePath: string;
  fileSize: number;
  fileType: string;
  uploadedAt: string;
}

interface Props {
  clientId: string;
  userId: string;
  clientName?: string;
  clientEmail?: string;
  onAllRequiredUploaded?: (count: number) => void;
}

const REQUIRED_DOCS = [
  {
    id: 'corporate_resolution',
    label: 'Corporate Resolution',
    description: 'Board resolution specifically authorizing the designated individual to execute the Power of Attorney on behalf of the corporation.',
    required: true,
    icon: 'ri-file-text-line',
    color: 'text-[#FDB913] bg-[#FDB913]/15 border-[#FDB913]/40',
  },
  {
    id: 'articles_of_incorporation',
    label: 'Articles of Incorporation',
    description: 'Official formation documents of the corporation as filed with the home country\'s government authority.',
    required: true,
    icon: 'ri-building-2-line',
    color: 'text-sky-400 bg-sky-500/15 border-sky-500/40',
  },
  {
    id: 'certificate_good_standing',
    label: 'Certificate of Good Standing',
    description: 'Issued by the home country authority confirming the corporation is in good standing and legally authorized to conduct business.',
    required: true,
    icon: 'ri-award-line',
    color: 'text-green-400 bg-green-500/15 border-green-500/40',
  },
  {
    id: 'board_minutes',
    label: 'Board Minutes',
    description: 'Formal meeting minutes documenting the board\'s decision to authorize the POA execution.',
    required: false,
    icon: 'ri-file-list-2-line',
    color: 'text-violet-400 bg-violet-500/15 border-violet-500/40',
  },
  {
    id: 'apostille_authentication',
    label: 'Apostille / Authentication',
    description: 'Apostilled or authenticated versions of documents as required under the Hague Apostille Convention or bilateral treaty.',
    required: true,
    icon: 'ri-verified-badge-line',
    color: 'text-orange-400 bg-orange-500/15 border-orange-500/40',
  },
  {
    id: 'translation_foreign_docs',
    label: 'Certified Translation(s)',
    description: 'Certified English translations of all foreign-language documents provided above.',
    required: false,
    icon: 'ri-translate-2',
    color: 'text-teal-400 bg-teal-500/15 border-teal-500/40',
  },
  {
    id: 'proof_authority_home_country',
    label: 'Proof of Authority — Home Country',
    description: 'Documentation establishing the grantor\'s authority to act on behalf of the corporation under the home country\'s corporate law (e.g., notarized power of attorney from home country, corporate registry extract).',
    required: true,
    icon: 'ri-government-line',
    color: 'text-rose-400 bg-rose-500/15 border-rose-500/40',
  },
];

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB for legal documents
const ALLOWED_TYPES = [
  'application/pdf',
  'image/jpeg', 'image/png', 'image/webp', 'image/tiff',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_LABEL = 'PDF, JPG, PNG, TIFF, DOCX (max 15 MB)';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileIcon(type: string): string {
  if (type === 'application/pdf') return 'ri-file-pdf-line text-red-400';
  if (type.startsWith('image/')) return 'ri-image-line text-green-400';
  if (type.includes('word')) return 'ri-file-word-line text-[#FDB913]';
  return 'ri-file-text-line text-white/60';
}

export default function ForeignCorpDocuments({ clientId, userId, clientName, clientEmail, onAllRequiredUploaded }: Props) {
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [activeUpload, setActiveUpload] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [adminNotifiedAt, setAdminNotifiedAt] = useState<number>(0);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const getDocsForRequirement = (reqId: string) =>
    uploadedDocs.filter(d => d.requirement === reqId);

  const isRequirementMet = (reqId: string) =>
    getDocsForRequirement(reqId).length > 0;

  const requiredCount = REQUIRED_DOCS.filter(d => d.required).length;
  const requiredMet = REQUIRED_DOCS.filter(d => d.required && isRequirementMet(d.id)).length;
  const allRequiredMet = requiredMet === requiredCount;

  // Notify admin when all required docs are uploaded (once per session)
  useEffect(() => {
    if (!allRequiredMet || adminNotifiedAt > 0 || !clientName || !clientEmail) return;
    const now = Date.now();
    setAdminNotifiedAt(now);
    const docTypesList = REQUIRED_DOCS.filter(d => d.required).map(d => d.label).join(', ');
    supabase.functions.invoke('send-admin-notification', {
      body: {
        eventType: 'foreign_corp_docs_uploaded',
        clientName,
        clientEmail,
        docCount: uploadedDocs.length,
        docTypes: docTypesList,
        timestamp: new Date().toISOString(),
      },
    }).catch(() => { /* non-fatal */ });
    supabase.from('client_notifications').insert({
      client_id: clientId,
      title: 'Supporting Documents Uploaded',
      message: 'All required foreign corporation supporting documents have been uploaded. You may now submit your CBP Form 5291 (Power of Attorney).',
      notification_type: 'info',
      is_read: false,
    }).catch(() => { /* non-fatal */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRequiredMet]);

  const handleFileSelect = async (reqId: string, file: File) => {
    setUploadErrors(prev => ({ ...prev, [reqId]: '' }));

    if (!ALLOWED_TYPES.includes(file.type)) {
      setUploadErrors(prev => ({ ...prev, [reqId]: 'File type not supported. Use PDF, JPG, PNG, TIFF, or DOCX.' }));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setUploadErrors(prev => ({ ...prev, [reqId]: 'File size exceeds 15 MB limit.' }));
      return;
    }

    setUploading(true);
    setActiveUpload(reqId);
    try {
      const timestamp = Date.now();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const filePath = `${userId}/foreign_corp/${reqId}_${timestamp}_${safeFileName}`;

      const { error: storageError } = await supabase.storage
        .from('case-documents')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });
      if (storageError) throw storageError;

      const { data: dbData, error: dbError } = await supabase
        .from('case_documents')
        .insert({
          client_id: clientId,
          file_name: file.name,
          file_path: filePath,
          file_size: file.size,
          file_type: file.type,
          description: `Foreign Corp POA Support: ${REQUIRED_DOCS.find(r => r.id === reqId)?.label}`,
          document_category: 'foreign_corp_poa',
        })
        .select()
        .single();

      if (dbError) throw dbError;

      const newDoc: UploadedDoc = {
        id: (dbData as { id: string }).id,
        requirement: reqId,
        fileName: file.name,
        filePath,
        fileSize: file.size,
        fileType: file.type,
        uploadedAt: new Date().toISOString(),
      };

      setUploadedDocs(prev => {
        const updated = [...prev, newDoc];
        if (onAllRequiredUploaded) onAllRequiredUploaded(updated.length);
        return updated;
      });

      const inputEl = fileInputRefs.current[reqId];
      if (inputEl) inputEl.value = '';
    } catch (err: unknown) {
      setUploadErrors(prev => ({
        ...prev,
        [reqId]: err instanceof Error ? err.message : 'Upload failed. Please try again.',
      }));
    } finally {
      setUploading(false);
      setActiveUpload(null);
    }
  };

  const handleRemove = async (doc: UploadedDoc) => {
    try {
      await supabase.storage.from('case-documents').remove([doc.filePath]);
      await supabase.from('case_documents').delete().eq('id', doc.id);
      setUploadedDocs(prev => prev.filter(d => d.id !== doc.id));
    } catch (err) {
      console.error('Remove failed:', err);
    }
  };

  return (
    <div className="border-2 border-orange-500/40 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500/20 to-orange-500/5 px-5 py-4 border-b border-orange-500/30">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 flex items-center justify-center bg-orange-500/20 border border-orange-500/40 rounded-lg flex-shrink-0">
            <i className="ri-government-line text-orange-400 text-xl"></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white font-black text-sm">Foreign Corporation / Entity — Required Supporting Documents</p>
              <span className="bg-orange-500/20 border border-orange-500/40 text-orange-400 text-[10px] font-black px-2 py-0.5 rounded-full uppercase tracking-wide whitespace-nowrap">
                19 CFR 141.32(b)
              </span>
            </div>
            <p className="text-white/55 text-xs mt-1 leading-relaxed">
              Because your corporation has not qualified to conduct business under U.S. law, CBP requires
              supporting documentation establishing the grantor&apos;s authority to execute this Power of Attorney.
              Upload all required documents below.
            </p>
          </div>
        </div>

        {/* Progress */}
        <div className="mt-4 flex items-center gap-3">
          <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-400 to-[#FDB913] rounded-full transition-all duration-500"
              style={{ width: `${requiredCount > 0 ? (requiredMet / requiredCount) * 100 : 0}%` }}
            />
          </div>
          <span className="text-white/60 text-xs font-bold whitespace-nowrap">
            {requiredMet}/{requiredCount} required
          </span>
          {allRequiredMet && (
            <span className="flex items-center gap-1 text-green-400 text-xs font-black whitespace-nowrap">
              <i className="ri-checkbox-circle-fill"></i>Complete
            </span>
          )}
        </div>
      </div>

      {/* Document requirements list */}
      <div className="bg-[#0a1118] divide-y divide-white/5">
        {REQUIRED_DOCS.map((req) => {
          const docs = getDocsForRequirement(req.id);
          const met = docs.length > 0;
          const isUploading = uploading && activeUpload === req.id;

          return (
            <div key={req.id} className={`px-5 py-4 transition-colors ${met ? 'bg-green-500/3' : ''}`}>
              <div className="flex items-start gap-3">
                {/* Status icon */}
                <div className={`w-8 h-8 flex items-center justify-center rounded-full border-2 flex-shrink-0 mt-0.5 transition-all ${
                  met
                    ? 'border-green-500 bg-green-500/20'
                    : req.required
                      ? 'border-orange-500/50 bg-orange-500/10'
                      : 'border-white/20 bg-white/5'
                }`}>
                  {met
                    ? <i className="ri-checkbox-circle-fill text-green-400 text-sm"></i>
                    : req.required
                      ? <i className="ri-error-warning-line text-orange-400 text-sm"></i>
                      : <i className="ri-checkbox-blank-circle-line text-white/30 text-sm"></i>
                  }
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <div className={`w-6 h-6 flex items-center justify-center rounded border ${req.color} flex-shrink-0`}>
                      <i className={`${req.icon} text-xs`}></i>
                    </div>
                    <p className="text-white font-black text-sm">{req.label}</p>
                    {req.required && (
                      <span className="text-red-400 text-[10px] font-black uppercase tracking-wide">Required</span>
                    )}
                    {!req.required && (
                      <span className="text-white/30 text-[10px] uppercase tracking-wide">Recommended</span>
                    )}
                  </div>
                  <p className="text-white/45 text-xs leading-relaxed mb-3">{req.description}</p>

                  {/* Uploaded files for this requirement */}
                  {docs.length > 0 && (
                    <div className="space-y-1.5 mb-3">
                      {docs.map(doc => (
                        <div key={doc.id} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className={`${getFileIcon(doc.fileType)} text-sm`}></i>
                          </div>
                          <span className="text-white/80 text-xs font-bold truncate flex-1">{doc.fileName}</span>
                          <span className="text-white/30 text-xs whitespace-nowrap">{formatFileSize(doc.fileSize)}</span>
                          <button
                            onClick={() => handleRemove(doc)}
                            className="w-5 h-5 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors cursor-pointer flex-shrink-0"
                            title="Remove"
                          >
                            <i className="ri-close-line text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Upload button */}
                  <div
                    className={`relative flex items-center gap-2 border border-dashed rounded-lg px-3 py-2.5 transition-all cursor-pointer ${
                      dragOver === req.id
                        ? 'border-[#FDB913] bg-[#FDB913]/10'
                        : met
                          ? 'border-green-500/30 hover:border-green-500/50 bg-green-500/5'
                          : 'border-white/15 hover:border-white/30'
                    }`}
                    onClick={() => { if (!isUploading) { const el = fileInputRefs.current[req.id]; if (el) el.click(); } }}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(req.id); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(null);
                      const file = e.dataTransfer.files[0];
                      if (file) handleFileSelect(req.id, file);
                    }}
                  >
                    {isUploading ? (
                      <><i className="ri-loader-4-line animate-spin text-[#FDB913] text-sm"></i>
                      <span className="text-[#FDB913] text-xs font-bold">Uploading...</span></>
                    ) : (
                      <>
                        <i className={`${met ? 'ri-upload-cloud-line text-green-400' : 'ri-upload-cloud-2-line text-white/40'} text-sm`}></i>
                        <span className={`text-xs font-bold ${met ? 'text-green-400' : 'text-white/50'}`}>
                          {met ? 'Upload additional file' : 'Click or drag to upload'}
                        </span>
                        <span className="text-white/25 text-[10px] ml-auto whitespace-nowrap">{ALLOWED_LABEL}</span>
                      </>
                    )}
                    <input
                      ref={(el) => { fileInputRefs.current[req.id] = el; return undefined; }}
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.tif,.tiff,.doc,.docx"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) handleFileSelect(req.id, f);
                      }}
                    />
                  </div>

                  {uploadErrors[req.id] && (
                    <p className="text-red-400 text-xs mt-1.5 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{uploadErrors[req.id]}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <div className="px-5 py-3 border-t border-white/10 bg-white/3">
        <p className="text-white/35 text-[10px] leading-relaxed">
          <i className="ri-information-line mr-1"></i>
          <strong className="text-white/50">19 CFR 141.32(b):</strong> When the power of attorney is executed by a corporation that has not qualified to conduct business
          under the laws of the United States, it shall be supported by documentation establishing the authority of the person executing the power of attorney.
          All foreign-language documents must be accompanied by certified English translations.
        </p>
      </div>
    </div>
  );
}
